<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <VueDatePicker
    :id="id"
    v-model="internalDateValue"
    v-tooltip="disabledCellsTooltip"
    :first-day-of-week="firstDayOfTheWeek"
    :locale="getLanguage()"
    :day-names="weekDays"
    :enable-time-picker="useTime"
    :auto-apply="autoClose"
    :placeholder="placeholder"
    :required="!allowEmpty"
    :clearable="allowEmpty && clearable"
    :disabled="disabled"
    :range="useRange"
    :format="format"
    :min-date="minDateString"
    :max-date="maxDateString"
    @update="$emit('input')" />
</template>

<script type="text/babel">
  import '@vuepic/vue-datepicker/dist/main.css';

  import VueDatePicker from '@vuepic/vue-datepicker';
  import { DateTime, Info } from 'luxon';
  import { capitalize } from 'vue';

  import colors from '@/Colors';
  import { getAppInstance, getLanguage } from '@/globals';
  import languages from '@/lang.ts';
  import i18n from '@/vueI18n';

  export default {
    components: {
      VueDatePicker,
    },
    props: {
      id: {
        type: String,
      },
      date: {
        type: Object,
      },
      minDate: {
        type: Object,
        default: undefined,
      },
      maxDate: {
        type: Object,
        default: undefined,
      },
      placeholder: {
        type: String,
        default: () => capitalize(i18n.global.t('messages.selectDate')),
      },
      disabledCellsTooltip: {
        type: String,
        default: null,
      },
      useCurrentDate: {
        type: Boolean,
        default: true,
      },
      useRange: {
        type: Boolean,
        default: false,
      },
      useTime: {
        type: Boolean,
        default: false,
      },
      useDate: {
        type: Boolean,
        default: true,
      },
      allowEmpty: {
        type: Boolean,
        default: true,
      },
      autoClose: {
        type: Boolean,
        default: true,
      },
      noButton: {
        type: Boolean,
        default: false,
      },
      inputSize: {
        type: String,
        default: null,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      clearable: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        colors,
        isReady: false,
        DateTime,
        prevDateValue: null,
        internalDateValue: null,
      };
    },
    computed: {
      isEmpty() {
        return this.date === null;
      },
      format() {
        // if (this.useTime && !this.useDate && !this.useRange) {
        //   return 'LT';
        // }
        // if (this.useTime && this.useDate) {
        //   return 'L LT';
        // }
        return null;
      },
      firstDayOfTheWeek() {
        const appLanguage = getLanguage();
        return languages.find((x) => x.value === appLanguage).firstDayOfTheWeek;
      },
      minDateString() {
        return this.minDate != null ? `${this.minDate.toISODate()}T${this.minDate.toISOTime()}` : undefined;
      },
      maxDateString() {
        return this.maxDate != null ? `${this.maxDate.toISODate()}T${this.maxDate.toISOTime()}` : undefined;
      },
      weekDays() {
        return Info.weekdays('short');
      },
    },
    watch: {
      internalDateValue(val) {
        if (val != null) {
          if (val instanceof Array) {
            // Date range
            const start = val[0] instanceof Date ? DateTime.fromJSDate(val[0]) : DateTime.fromISO(val[0]);
            const end = val[1] instanceof Date ? DateTime.fromJSDate(val[1]) : DateTime.fromISO(val[1]);
            this.$emit('rangeChanged', {
              start,
              end,
            });
          } else {
            const date = val instanceof Date ? DateTime.fromJSDate(val) : DateTime.fromISO(val);
            this.updateDate(date);
          }
        } else {
          this.$emit('dateChanged', null);
          this.$emit('rangeChanged', {
            start: null,
            end: null,
          });
        }
      },
      date(val) {
        if (
          val !== this.internalDate &&
          (val == null || this.internalDate == null || !val.equals(this.internalDateValue))
        ) {
          this.internalDateValue = val != null ? `${val.toISODate()}T${val.toISOTime()}` : null;
        }
      },
    },
    mounted() {
      this.prevDateValue = this.date;
      //
      // This trick is to avoid issues whenever this.date is null on initial load.
      // See bug report: https://github.com/chronotruck/vue-ctk-date-time-picker/issues/247
      //
      const date = DateTime.local();
      if (this.useCurrentDate) {
        if (this.useRange) {
          const datePlusOneWeek = date.plus({ week: 1 });
          this.internalDateValue = [
            `${date.toISODate()}T${date.toISOTime()}`,
            `${datePlusOneWeek.toISODate()}T${datePlusOneWeek.toISOTime()}`,
          ];
        } else {
          this.internalDateValue = `${date.toISODate()}T${date.toISOTime()}`;
        }
      }
      setTimeout(() => {
        this.isReady = true;
        if (this.date != null) {
          this.internalDateValue = `${this.date.toISODate()}T${this.date.toISOTime()}`;
        }
      }, 10);
    },
    methods: {
      getLanguage,
      getAppInstance,
      capitalize,
      updateDate(newValue) {
        if (!this.isReady || (newValue != null && !newValue.isValid)) {
          return;
        }
        if (!this.prevDateValue || (this.prevDateValue && !newValue.equals(this.prevDateValue))) {
          this.$emit('dateChanged', newValue);
        }
        this.prevDateValue = newValue;
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss">
  @use '@/styles/datepicker';

  .dp__input {
    --dp-font-size: 1.3rem;
  }
</style>
